import { Copy, Check, FloppyDisk, CaretDown, CaretUp, FilePdf, FileText, GoogleLogo, CodeSimple, SpinnerGap, Cloud, CloudCheck, CloudSlash } from "@phosphor-icons/react";

export function SectionActionBar({
  copiedAll, onCopyAll, onSave, saving, isSaved,
  sectionNames, collapsedSections, onToggleAll,
  onExportPdf, exporting,
  onCopyMarkdown, copiedMarkdown,
  onOpenGoogleDocs, gdocsExporting,
  onCopyHtml, copiedHtml,
  autosaveStatus, lastSavedTime, hasUnsavedEdits,
  formatTime,
}) {
  const allCollapsed = sectionNames.every((n) => collapsedSections[n]);

  return (
    <div className="sticky top-16 z-40 bg-[var(--bg-base)]/90 backdrop-blur-xl border border-[var(--border)] rounded-lg p-2 sm:p-3 flex flex-wrap gap-1.5 sm:gap-2 items-center justify-between">
      <div className="flex gap-1.5 sm:gap-2 flex-wrap">
        <button
          onClick={onCopyAll}
          className="flex items-center gap-1.5 px-2.5 sm:px-4 py-2 rounded-md bg-[#FF4400] text-white text-xs sm:text-sm font-medium hover:bg-[#FF6633] transition-colors duration-200"
          data-testid="copy-all-sections-button"
        >
          {copiedAll ? <Check size={16} /> : <Copy size={16} />}
          <span className="hidden sm:inline">{copiedAll ? "Copied!" : "Copy All"}</span>
        </button>
        {onSave && !isSaved && (
          <button
            onClick={onSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-2.5 sm:px-4 py-2 rounded-md bg-[var(--bg-raised)] text-[var(--text-secondary)] text-xs sm:text-sm font-medium hover:text-[var(--text-primary)] border border-[var(--border-strong)] transition-colors duration-200 disabled:opacity-50"
            data-testid="save-multi-section-button"
          >
            <FloppyDisk size={16} />
            <span className="hidden sm:inline">{saving ? "Saving..." : "Save"}</span>
          </button>
        )}
        {isSaved && (
          <span className="flex items-center gap-1.5 px-2.5 py-2 text-xs sm:text-sm text-[var(--success)]">
            <Check size={16} /> <span className="hidden sm:inline">Saved</span>
          </span>
        )}
      </div>
      <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
        <button onClick={onToggleAll}
          className="flex items-center gap-1 px-2 sm:px-3 py-2 rounded-md bg-[var(--bg-raised)] text-[var(--text-secondary)] text-xs font-medium hover:text-[var(--text-primary)] border border-[var(--border-strong)] transition-colors duration-200"
          data-testid="toggle-all-sections-button"
        >
          {allCollapsed ? (<><CaretDown size={14} /> <span className="hidden sm:inline">Expand All</span></>) : (<><CaretUp size={14} /> <span className="hidden sm:inline">Collapse All</span></>)}
        </button>
        <button onClick={onExportPdf} disabled={exporting}
          className="flex items-center gap-1.5 px-2 sm:px-3 py-2 rounded-md bg-[var(--bg-raised)] text-[var(--text-secondary)] text-xs sm:text-sm font-medium hover:text-red-400 border border-[var(--border-strong)] hover:border-red-400/30 transition-colors duration-200 disabled:opacity-50"
          data-testid="export-pdf-button">
          <FilePdf size={16} /> <span className="hidden sm:inline">{exporting ? "..." : "PDF"}</span>
        </button>
        <button onClick={onCopyMarkdown}
          className="flex items-center gap-1.5 px-2 sm:px-3 py-2 rounded-md bg-[var(--bg-raised)] text-[var(--text-secondary)] text-xs sm:text-sm font-medium hover:text-[var(--text-primary)] border border-[var(--border-strong)] transition-colors duration-200"
          data-testid="copy-markdown-button">
          {copiedMarkdown ? <Check size={16} /> : <FileText size={16} />} <span className="hidden sm:inline">{copiedMarkdown ? "Copied!" : "MD"}</span>
        </button>
        <button onClick={onOpenGoogleDocs} disabled={gdocsExporting}
          className="flex items-center gap-1.5 px-2 sm:px-3 py-2 rounded-md bg-[var(--bg-raised)] text-[var(--text-secondary)] text-xs sm:text-sm font-medium hover:text-blue-400 border border-[var(--border-strong)] hover:border-blue-400/30 transition-colors duration-200 disabled:opacity-50"
          data-testid="open-google-docs-button">
          {gdocsExporting ? <SpinnerGap size={16} className="animate-spin" /> : <GoogleLogo size={16} />} <span className="hidden sm:inline">{gdocsExporting ? "Exporting..." : "Docs"}</span>
        </button>
        <button onClick={onCopyHtml}
          className="flex items-center gap-1.5 px-2 sm:px-3 py-2 rounded-md bg-[var(--bg-raised)] text-[var(--text-secondary)] text-xs sm:text-sm font-medium hover:text-orange-400 border border-[var(--border-strong)] hover:border-orange-400/30 transition-colors duration-200"
          data-testid="copy-html-button">
          {copiedHtml ? <Check size={16} /> : <CodeSimple size={16} />} <span className="hidden sm:inline">{copiedHtml ? "Copied!" : "HTML"}</span>
        </button>
        <span className="text-[10px] sm:text-xs font-['JetBrains_Mono'] text-[var(--text-muted)] hidden sm:inline">
          {sectionNames.length} SECTIONS
        </span>
        {!isSaved && autosaveStatus && (
          <span className="flex items-center gap-1.5 text-xs text-[var(--text-muted)]" data-testid="autosave-status">
            {autosaveStatus === "saving" && <><Cloud size={12} className="animate-pulse" /> Saving draft...</>}
            {autosaveStatus === "saved" && <><CloudCheck size={12} className="text-[var(--success)]" /> Draft saved {formatTime(lastSavedTime)}</>}
            {autosaveStatus === "error" && <><CloudSlash size={12} className="text-amber-400" /> Draft not saved</>}
          </span>
        )}
        {hasUnsavedEdits && !autosaveStatus && (
          <span className="flex items-center gap-1.5 text-xs text-amber-400" data-testid="unsaved-edits-indicator">
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            Unsaved edits
          </span>
        )}
      </div>
    </div>
  );
}
